import { GetListParams } from 'react-admin'
import { axiosDZAuthInstance } from '../axios'
import { DASHBOARD } from '../routes'
import { formatErrorMessage, mapValidationErrorsByProperty, responseMapper } from '../helpers'
import { TMeta } from '@/types/data'

const VARIANT_COSTS_PATH = `${DASHBOARD()}/variant-costs`
const VARIANT_COST_CURRENCIES_URL = `${VARIANT_COSTS_PATH}/currencies`

export type TVariantCostCurrency = {
  code: string
  name: string
}

export type TVariantCostBulkValidationError = {
  row: number
  field: string
  message: string
  value?: string | number
  errorCode?:
    | 'INVALID_VARIANT_ID'
    | 'INVALID_SUPPLIER_ID'
    | 'INVALID_CURRENCY_CODE'
    | 'DUPLICATE_ROW_IN_FILE'
    | 'ALREADY_EXISTS'
    | 'INVALID_UPLOADED_BY'
    | 'NOT_FOUND'
    | 'DUPLICATE_ID_IN_FILE'
    | 'NO_UPDATE_FIELDS'
    | 'DUPLICATE_TARGET_COMBINATION_IN_FILE'
    | 'TARGET_COMBINATION_ALREADY_EXISTS'
    | string
}

export type TVariantCostBulkValidateResponse = {
  isValid: boolean
  totalCount: number
  validCount: number
  invalidCount: number
  validationErrors: TVariantCostBulkValidationError[]
  message: string
}

export type TVariantCostBulkUploadResponse = {
  success: boolean
  totalCount: number
  createdCount: number
  failedCount: number
  validationErrors: TVariantCostBulkValidationError[]
  createdVariantCosts?: Array<{
    id: number
    variantId: number
    supplierId: string
  }>
  message: string
}

interface TVariantCostsListResponse {
  data: unknown[]
  meta: TMeta
}

type ApiConflictBody = {
  statusCode?: number
  message?: string | unknown
}

export class VariantCostRequestError extends Error {
  declare readonly statusCode: number
  declare readonly fieldErrors?: Record<string, string>

  constructor(
    message: string,
    init: {
      statusCode: number
      fieldErrors?: Record<string, string>
    },
  ) {
    super(message)
    this.name = 'VariantCostRequestError'
    this.statusCode = init.statusCode
    this.fieldErrors = init.fieldErrors
  }
}

export type TVariantCostCreatePayload = {
  variantId: number
  supplierId: string
  cost: number
  currencyCode: string
  effectiveDate: string
}

const toIso = (v: unknown): string => {
  if (v instanceof Date) return v.toISOString()
  if (typeof v === 'string' && v.trim() !== '') return v
  return ''
}

export const variantCostsParamsMapper = (params: GetListParams) => {
  const { filter = {}, pagination, sort } = params
  const page = pagination?.page ?? 1
  const perPage = pagination?.perPage ?? 10
  const field = sort?.field ?? 'effectiveDate'
  const order = sort?.order ?? 'DESC'

  const query: Record<string, string | number> = {
    offset: (page - 1) * perPage,
    limit: perPage,
    sortBy: field,
    sortingDirection: order.toLowerCase(),
  }

  const variantId = filter.variantId
  if (variantId !== '' && variantId != null && !Number.isNaN(Number(variantId))) {
    query.variantId = Number(variantId)
  }

  const supplierRaw = filter.supplierId
  if (supplierRaw != null && String(supplierRaw).trim() !== '') {
    query.supplierId = String(supplierRaw).trim().toUpperCase()
  }

  const currencyRaw = filter.currencyCode
  if (currencyRaw != null && String(currencyRaw).trim() !== '') {
    query.currencyCode = String(currencyRaw).trim().toUpperCase()
  }

  const uploadedBy = filter.uploadedBy
  if (uploadedBy != null && String(uploadedBy).trim() !== '') {
    query.uploadedBy = String(uploadedBy).trim()
  }

  const rangeKeys = [
    'effectiveDateFrom',
    'effectiveDateTo',
    'createdAtFrom',
    'createdAtTo',
  ] as const

  for (const key of rangeKeys) {
    const raw = filter[key]
    if (raw === '' || raw == null) continue
    const iso = toIso(raw)
    if (iso) query[key] = iso
  }

  return query
}

export const getVariantCostCurrencies = async (): Promise<TVariantCostCurrency[]> => {
  try {
    const { data } = await axiosDZAuthInstance.get<TVariantCostCurrency[]>(VARIANT_COST_CURRENCIES_URL)
    return Array.isArray(data) ? data : []
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: unknown } } }
    const raw = err.response?.data?.message
    if (raw !== undefined) {
      throw new Error(formatErrorMessage(raw as Parameters<typeof formatErrorMessage>[0]))
    }
    throw new Error('Failed to load currencies')
  }
}

export const validateVariantCostBulkUpload = async (file: File): Promise<TVariantCostBulkValidateResponse> => {
  const formData = new FormData()
  formData.append('file', file, file.name || 'variant-costs.csv')

  try {
    const { data } = await axiosDZAuthInstance.post<TVariantCostBulkValidateResponse>(
      `${VARIANT_COSTS_PATH}/bulk/validate-upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
    return {
      ...data,
      validationErrors: Array.isArray(data.validationErrors) ? data.validationErrors : [],
    }
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: unknown } } }
    const raw = err.response?.data?.message
    if (raw !== undefined) {
      throw new Error(formatErrorMessage(raw as Parameters<typeof formatErrorMessage>[0]))
    }
    throw new Error('Failed to validate CSV file')
  }
}

export const uploadVariantCostBulk = async (file: File): Promise<TVariantCostBulkUploadResponse> => {
  const formData = new FormData()
  formData.append('file', file, file.name || 'variant-costs.csv')

  try {
    const { data } = await axiosDZAuthInstance.post<TVariantCostBulkUploadResponse>(
      `${VARIANT_COSTS_PATH}/bulk/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
    return {
      ...data,
      validationErrors: Array.isArray(data.validationErrors) ? data.validationErrors : [],
      createdVariantCosts: Array.isArray(data.createdVariantCosts) ? data.createdVariantCosts : [],
    }
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: unknown } } }
    const raw = err.response?.data?.message
    if (raw !== undefined) {
      throw new Error(formatErrorMessage(raw as Parameters<typeof formatErrorMessage>[0]))
    }
    throw new Error('Failed to upload CSV file')
  }
}

export const getVariantCost = async (id: number | string): Promise<Record<string, unknown> & { id: number }> => {
  try {
    const { data } = await axiosDZAuthInstance.get<Record<string, unknown>>(`${VARIANT_COSTS_PATH}/${id}`)
    const row = data
    const numericId = typeof row.id === 'number' ? row.id : Number(row.id)
    return { ...row, id: Number.isNaN(numericId) ? Number(id) : numericId }
  } catch (error: unknown) {
    const err = error as { response?: { status?: number; data?: ApiConflictBody } }
    throwVariantCostsAxiosFailure(err.response?.status, err.response?.data, 'getOne')
  }
}

export const getVariantCostsList = async (
  params: GetListParams,
): Promise<{ data: unknown[]; total: number }> => {
  try {
    const { data } = await axiosDZAuthInstance.get<TVariantCostsListResponse>(VARIANT_COSTS_PATH, {
      params: variantCostsParamsMapper(params),
    })
    return responseMapper(data)
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: unknown } } }
    const raw = err.response?.data?.message
    if (raw === undefined) {
      throw new Error('Failed to fetch variant costs')
    }
    throw new Error(formatErrorMessage(raw as Parameters<typeof formatErrorMessage>[0]))
  }
}

const conflictDuplicateMessage =
  'A variant cost already exists for this variant, supplier, and effective date.'

const resolveErrorMessage = (message: ApiConflictBody['message']): string => {
  if (typeof message === 'string') return message
  return formatErrorMessage(message as Parameters<typeof formatErrorMessage>[0])
}

type VariantCostRequestIntent = 'create' | 'getOne'

function throwVariantCostsAxiosFailure(
  status: number | undefined,
  data: ApiConflictBody | undefined,
  intent: VariantCostRequestIntent,
): never {
  if (status === 400 && data?.message !== undefined) {
    const summary = resolveErrorMessage(data.message)
    if (intent === 'getOne') {
      throw new VariantCostRequestError(summary || 'Request could not be completed.', {
        statusCode: 400,
      })
    }
    const fieldErrors = mapValidationErrorsByProperty(
      data.message as Parameters<typeof mapValidationErrorsByProperty>[0],
    )
    throw new VariantCostRequestError(summary || 'Validation failed', {
      statusCode: 400,
      fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined,
    })
  }

  if (status === 409) {
    const msg = resolveErrorMessage(data?.message)
    throw new VariantCostRequestError(msg || conflictDuplicateMessage, { statusCode: 409 })
  }

  if (status === 404) {
    const msg = resolveErrorMessage(data?.message)
    let fallback = 'Variant, supplier, currency, or admin was not found.'
    if (intent === 'getOne') fallback = 'Record not found'
    throw new VariantCostRequestError(msg || fallback, { statusCode: 404 })
  }

  if (data?.message !== undefined) {
    throw new VariantCostRequestError(resolveErrorMessage(data.message), {
      statusCode: status ?? 0,
    })
  }

  const fallbackVerb =
    intent === 'getOne'
      ? 'Failed to load variant cost'
      : 'Failed to create variant cost'
  throw new VariantCostRequestError(fallbackVerb, {
    statusCode: status ?? 0,
  })
}

export const mapFormDataToVariantCostPayload = (data: Record<string, unknown>): TVariantCostCreatePayload => {
  const raw = data.effectiveDate
  let effectiveDate: string
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) effectiveDate = raw.toISOString()
  else if (typeof raw === 'string' && raw.trim()) {
    const d = new Date(raw)
    effectiveDate = Number.isNaN(d.getTime()) ? raw.trim() : d.toISOString()
  } else {
    const d = new Date(String(raw ?? ''))
    effectiveDate = Number.isNaN(d.getTime()) ? String(raw ?? '') : d.toISOString()
  }

  return {
    variantId: Number(data.variantId),
    supplierId: String(data.supplierId ?? ''),
    cost: Number(data.cost),
    currencyCode: String(data.currencyCode ?? ''),
    effectiveDate,
  }
}

export const createVariantCost = async (
  payload: TVariantCostCreatePayload,
): Promise<Record<string, unknown>> => {
  const body = {
    variantId: payload.variantId,
    supplierId: payload.supplierId.trim().toUpperCase(),
    currencyCode: payload.currencyCode.trim().toUpperCase(),
    cost: payload.cost,
    effectiveDate: payload.effectiveDate,
  }

  try {
    const { data } = await axiosDZAuthInstance.post<Record<string, unknown>>(VARIANT_COSTS_PATH, body)
    return data
  } catch (error: unknown) {
    const err = error as { response?: { status?: number; data?: ApiConflictBody } }
    throwVariantCostsAxiosFailure(err.response?.status, err.response?.data, 'create')
  }
}
